'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, ChevronDown, CheckCircle2, Quote, MapPin } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { COUNTRY_TRANSLATIONS, type CountryText } from '@/core/i18n/content/country.content';
import { useLocaleContext } from '@/core/i18n/locale.context';
import { useLocaleStore } from '@/core/i18n/locale.store';
import { COUNTRY_CONFIGS, type CountryLandingConfig } from './seo-country.data';

export type { CountryLandingConfig } from './seo-country.data';

/* -------------------------------------------------------------------------- */
/*  Section -- Hero                                                           */
/* -------------------------------------------------------------------------- */

function CountryHero({ config, t }: { config: CountryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  return (
    <section id={`${pfx}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <div className="mb-4 flex items-center justify-center gap-2 text-sm text-hms-textSub">
          <MapPin className="h-4 w-4 text-hms-gold" />
          <span>{config.countryName}</span>
        </div>
        <h1
          id={`${pfx}-title`}
          className={cn(
            'font-poppins text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {config.heroTitle}
        </h1>
        <p id={`${pfx}-subtitle`} className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {config.heroSubtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`${pfx}-cta-demo`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.bookDemo}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Industry Stats                                                 */
/* -------------------------------------------------------------------------- */

function CountryStats({ config, t }: { config: CountryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  return (
    <section id={`${pfx}-stats`} className="relative w-full overflow-hidden bg-hms-surface py-16 sm:py-24">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2 id={`${pfx}-stats-title`} className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl">
          {t.seoTemplate.industryAtAGlance.replace('{0}', config.countryName)}
        </h2>
        <div className="mt-10 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {config.industryStats.map((stat, idx) => (
            <div
              key={idx}
              id={`${pfx}-stat-${idx}`}
              className="rounded-xl border border-hms-border bg-hms-card p-5 text-center"
            >
              <p className={cn(
                'text-2xl font-extrabold tracking-tight sm:text-3xl',
                'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
              )}>
                {stat.value}
              </p>
              <p className="mt-2 text-xs font-medium text-hms-textSub">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Features                                                       */
/* -------------------------------------------------------------------------- */

function CountryFeatures({ config, t }: { config: CountryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  return (
    <section id={`${pfx}-features`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 id={`${pfx}-features-title`} className={cn(
          'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {t.seoTemplate.purposeBuiltFor.replace('{0}', config.countryName)}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-hms-textSub">
          {t.seoTemplate.featureDesigned.replace('{0}', config.countryName)}
        </p>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {config.features.map((feat, idx) => {
            const Icon = feat.icon;
            return (
              <div
                key={feat.title}
                id={`${pfx}-feature-${idx}`}
                className={cn(
                  'group rounded-xl border border-hms-border bg-hms-card p-6',
                  'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
                )}
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-hms-gold/10 group-hover:bg-hms-gold/15 transition-colors">
                  <Icon className="h-5 w-5 text-hms-gold" />
                </div>
                <h3 className="mb-2 text-base font-bold text-hms-text tracking-tight">{feat.title}</h3>
                <p className="text-sm leading-relaxed text-hms-textSub">{feat.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Compliance                                                     */
/* -------------------------------------------------------------------------- */

function CountryComplianceSection({ config }: { config: CountryLandingConfig }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  return (
    <section id={`${pfx}-compliance`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2 id={`${pfx}-compliance-title`} className={cn(
          'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {config.complianceTitle}
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {config.complianceItems.map((item, idx) => (
            <div
              key={idx}
              id={`${pfx}-compliance-item-${idx}`}
              className="flex items-start gap-3 rounded-xl border border-hms-border bg-hms-card p-5"
            >
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hms-green/10">
                <CheckCircle2 className="h-3.5 w-3.5 text-hms-green" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-hms-text">{item.name}</h4>
                <p className="mt-1 text-xs leading-relaxed text-hms-textSub">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Testimonial                                                    */
/* -------------------------------------------------------------------------- */

function CountryTestimonialSection({ config }: { config: CountryLandingConfig }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  const t = config.testimonial;
  return (
    <section id={`${pfx}-testimonial`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-hms-border bg-hms-card p-8 text-center">
          <Quote className="mx-auto mb-4 h-8 w-8 text-hms-gold/20" />
          <p className="text-base italic leading-relaxed text-hms-textSub sm:text-lg">
            &ldquo;{t.quote}&rdquo;
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-hms-gold/20">
              <span className="text-xs font-bold text-hms-gold">{t.initials}</span>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-hms-text">{t.name}</p>
              <p className="text-xs text-hms-textDim">{t.role}, {t.hotel}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- FAQ                                                            */
/* -------------------------------------------------------------------------- */

function CountryFAQSection({ config, t }: { config: CountryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`${pfx}-faq`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2 id={`${pfx}-faq-title`} className={cn(
          'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => (
            <div
              key={idx}
              id={`${pfx}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx ? 'border-hms-gold/30 bg-hms-card' : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`${pfx}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span className={cn('text-sm font-semibold leading-snug', openIndex === idx ? 'text-hms-gold' : 'text-hms-text')}>
                  {item.question}
                </span>
                <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-200', openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim')} />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- CTA                                                            */
/* -------------------------------------------------------------------------- */

function CountryCTA({ config, t }: { config: CountryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  return (
    <section id={`${pfx}-cta`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2 id={`${pfx}-cta-title`} className={cn(
          'text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {config.ctaTitle}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">{config.ctaSubtitle}</p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-trial-btn`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`${pfx}-cta-contact-btn`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">{t.common.noCreditCard}</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Related Country Pages                                          */
/* -------------------------------------------------------------------------- */

function CountryRelated({ config, t, translations }: { config: CountryLandingConfig; t: TemplateChromeContent; translations: Record<string, CountryText> | undefined }) {
  const pfx = `seo-country-${config.countryCode.toLowerCase()}`;
  const otherCountries = Object.values(COUNTRY_CONFIGS).filter((c) => c.slug !== config.slug);
  return (
    <section id={`${pfx}-related`} className="relative w-full overflow-hidden bg-hms-surface py-16 sm:py-20">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl">
          {t.seoTemplate.availableInMoreRegions}
        </h2>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {otherCountries.map((c, idx) => {
            const relKey = c.slug.replace('hotel-management-software-', '');
            const relName = translations?.[relKey]?.countryName ?? c.countryName;
            return (
              <LocaleLink
                key={c.slug}
                id={`${pfx}-related-${idx}`}
                href={`/${c.slug}`}
                className={cn(
                  'group flex items-center justify-center gap-2 rounded-xl border border-hms-border bg-hms-card px-4 py-4',
                  'transition-all duration-200 hover:border-hms-gold/20 hover:-translate-y-0.5',
                )}
              >
                <MapPin className="h-3.5 w-3.5 text-hms-gold" />
                <span className="text-sm font-semibold text-hms-text group-hover:text-hms-gold transition-colors">
                  {relName}
                </span>
              </LocaleLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

function useCountryLocale(): { text: CountryText | undefined; allTexts: Record<string, CountryText> | undefined; resolveKey: (k: string) => CountryText | undefined } {
  const ctxLocale = useLocaleContext();
  const storeLocale = useLocaleStore((s) => s.locale);
  const locale = ctxLocale ?? storeLocale;
  const localeTexts = locale !== 'en' ? COUNTRY_TRANSLATIONS[locale] : undefined;
  return {
    text: undefined,
    allTexts: localeTexts,
    resolveKey: (k: string) => localeTexts?.[k],
  };
}

function mergeConfig(config: CountryLandingConfig, text: CountryText | undefined): CountryLandingConfig {
  if (!text) return config;
  return {
    ...config,
    countryName: text.countryName,
    heroTitle: text.heroTitle,
    heroSubtitle: text.heroSubtitle,
    industryStats: config.industryStats.map((s, i) => ({ ...s, label: text.statsLabels[i] ?? s.label })),
    features: config.features.map((f, i) => ({
      ...f,
      title: text.features[i]?.title ?? f.title,
      description: text.features[i]?.description ?? f.description,
    })),
    complianceTitle: text.complianceTitle,
    complianceItems: config.complianceItems.map((c, i) => ({
      ...c,
      name: text.complianceItems[i]?.name ?? c.name,
      description: text.complianceItems[i]?.description ?? c.description,
    })),
    testimonial: {
      ...config.testimonial,
      quote: text.testimonial.quote,
      role: text.testimonial.role,
      hotel: text.testimonial.hotel,
    },
    faqItems: config.faqItems.map((f, i) => ({
      question: text.faqItems[i]?.question ?? f.question,
      answer: text.faqItems[i]?.answer ?? f.answer,
    })),
    ctaTitle: text.ctaTitle,
    ctaSubtitle: text.ctaSubtitle,
  };
}

export function CountryLanding({ slug }: { slug: string }) {
  const key = slug.replace('hotel-management-software-', '');
  const baseConfig = COUNTRY_CONFIGS[key];
  const t = usePageContent(TEMPLATE_CHROME);
  const { allTexts, resolveKey } = useCountryLocale();
  if (!baseConfig) return null;
  const config = mergeConfig(baseConfig, resolveKey(key));
  return (
    <main id={`seo-country-${config.countryCode.toLowerCase()}-page`}>
      <CountryHero config={config} t={t} />
      <CountryStats config={config} t={t} />
      <CountryFeatures config={config} t={t} />
      <CountryComplianceSection config={config} />
      <CountryTestimonialSection config={config} />
      <CountryFAQSection config={config} t={t} />
      <CountryRelated config={config} t={t} translations={allTexts} />
      <CountryCTA config={config} t={t} />
    </main>
  );
}
